<template>
    <div class="pathways-column-mapping">
        <section v-for="(column, i) in mapping.slice(0,2)" :key="column?.id || i"
            :id="i == 0 ? 'primary-key-column-mapping' : 'column-mapping'">
            <div v-if="i==0 && showColumn(column, i)" class="w-100 d-flex flex-column gap-2 mb-3">
                <strong>Primary Key Column(s) 
                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip"
                        title="The primary key is what determines a unique row of data and how that aligns to the destination. It may be as simple as the Contact ID or an email. For advertising destinations like Facebook and Google, we use multiple columns to sync every possible bit of information." alt=""></img>
                </strong>
                <div class="mapping row" id="primary-key-column" :class="{ 'is-invalid' : errors[i]?.source != '' }">
                    <div :class="(local_source?.operation != 'append' ? 'col-md-6 col-12' : 'col-12')">
                        <label>{{ local_source?.data_source?.name || 'Source' }} Source Column(s)</label>
                        <accordion-multi-select v-model="column.source_column" @input="selectKeyColumn($event, i)"
                            :class="{'is-invalid' : errors[i]?.source != ''}" :options="local_pk_source_columns"
                            :key="column?.id || i" :field_name="'primary_key_column'"
                            :model-value="Array.isArray(column.source_column) ? column.source_column : [column.source_column]"
                            :if_allow_edit="if_allow_edit"></accordion-multi-select>
                        <div class="text-danger invalid-feedback" v-if="errors[i]?.source != ''">
                            {{ errors[i].source }}
                        </div>
                    </div>

                    <div v-if="local_source?.operation != 'append'" class="col-md-6 col-12 mt-2 mt-md-0">
                        <label>{{ destination?.integration?.display_name || 'Destination' }} Column </label>
                        <input type="text" class="form-control" v-if="hasOneKeyOption()"
                            :value="getColumnText(column.destination_column)" disabled="disabled" aria-label="Text input">
                        <accordion-select v-else v-model="column.destination_column"
                            :class="{'is-invalid' : errors[i]?.destination != ''}"
                            :options="getPrimayKeyDestinations(column.destination_column)"
                            :key="'dest'+(column?.id || i)" :if_allow_edit="if_allow_edit"></accordion-select>
                        <div class="text-danger invalid-feedback" v-if="errors[i]?.destination != ''">
                            {{ errors[i].destination }}
                        </div>
                    </div>
                </div>
                <div v-if="!if_on_admin_screen" class="mapping" id="prioritization-columns">
                    <div class="row">
                        <div class="col-12">
                            <label>Prioritization Column(s)</label><br>
                            <small class="mb-2 d-block text-muted">If there are multiple records with the same primary
                                key, how should Avid determine which one to sync?</small>
                        </div>
                        <div class="col-12 col-lg-9 mb-2"
                            :class="prioritization_column.length > 1 ? 'd-grid' : 'd-flex'">
                            <template v-for="(priority, i) in prioritization_column">
                                <b v-if="prioritization_column.length > 1" class="fs-5">{{ i+1 }}.</b>
                                <div class="input-group position-relative">
                                    <accordion-select v-model="priority.column" :options="source_columns"
                                        :if_allow_edit="if_allow_edit"></accordion-select>
                                    <button class="btn btn-secondary dropdown-toggle" type="button"
                                        data-bs-toggle="dropdown" aria-expanded="false" :disabled="!if_allow_edit">{{
                                        priority.sort == 'DESC'? 'Descending' : 'Ascending' }}</button>
                                    <ul class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                        <li><a class="dropdown-item" href="#"
                                                @click.prevent="priority.sort = 'ASC'">Ascending</a></li>
                                        <li><a class="dropdown-item" href="#"
                                                @click.prevent="priority.sort = 'DESC'">Descending</a></li>
                                    </ul>
                                </div>
                                <button v-if="prioritization_column.length > 1 && if_allow_edit"
                                    class="mapping-grid__button btn btn-primary btn-icon-only-sm"
                                    @click="prioritization_column.splice(i, 1);">
                                    <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                </button>
                                <div v-else-if="prioritization_column.length > 1 && !if_allow_edit"
                                    class="d-inline-block" data-bs-toggle="tooltip"
                                    title="You do not have privileges to complete this action">
                                    <button class="mapping-grid__button btn btn-secondary btn-icon-only-sm" :disabled="true">
                                        <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                    </button>
                                </div>
                            </template>
                        </div>
                        <div class="col-12 mt-2">
                            <button v-if="if_allow_edit" class="btn btn-secondary btn-sm"
                                @click="addPriorityColumn()"><i class="fas fa-plus"></i> Add Prioritization</button>
                            <div v-else class="d-inline-block" data-bs-toggle="tooltip"
                                title="You do not have privileges to complete this action">
                                <button class="btn btn-secondary btn-sm" :disabled="true"><i class="fas fa-plus"></i>
                                    Add Prioritization</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="i>=1 && showAdditionalColumns()" class="w-100 d-flex flex-column gap-2">
                <div v-if="i==1">
                    <strong>Additional Columns</strong>
                    <p>Use the fields below to map the columns from your data source to those available in {{
                        destination?.integration?.name || 'destination' }}.
                        <strong v-if="!if_on_admin_screen">If you have recently added new columns to your destination,
                            <a href="#" @click.prevent="refreshColumns()">click here to refresh the available column
                                list.</a></strong>
                    </p>
                </div>

                <!-- Large screens (lg and above) : keep existing table layout -->
                <div class="row">
                    <div class="col mb-3">
                        <div class="card p-0 position-relative">
                            <button class="btn btn-secondary btn-icon-only-md d-none d-md-block position-absolute" 
                                    @click="toggleExpand" 
                                    data-bs-toggle="tooltip" 
                                    title="Toggle full width" 
                                    v-if="!if_on_admin_screen && slicedMapping.length > 0"
                                    style="right: 12px; top: 5px;">
                                <img :src="is_expanded ? '/img/icons/avid-icons/minimize-4.svg' : '/img/icons/avid-icons/maximize-4.svg'" style="width:14px;" class="icon pe-0" alt="">
                            </button>
                            <table class="column-mapping-table mapping-table">
                                <thead class="w-100">
                                    <tr>
                                        <th>
                                            <div class="flex-column">
                                                <h4 class="ms-2 mb-1">Column Name You're Importing</h4>
                                            </div>
                                        </th>
                                        <th>
                                        </th>
                                        <th>
                                            <div class="flex-column">
                                                <h4 class="ms-2 mb-1">{{ destination?.integration?.name || 'Destination' }}
                                                    Column</h4>
                                            </div>
                                        </th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody class="w-100 my-2">
                                    <template v-for="(column, index) in slicedMapping" :key="column?.id || index">
                                        <tr v-if="showColumn(column, index+1)" class="mb-2">
                                            <td class="mapping-grid__source">
                                                <select v-if="isDropdown(column) && !if_on_admin_screen"
                                                    v-model="column.source_column.value" class="form-select"
                                                    :disabled="!if_allow_edit" aria-label="Value">
                                                    <option v-for="option in dropdowns[column.destination_column]"
                                                        :value="option.id">{{ option.name }}</option>
                                                </select>
                                                <div class="input-group"
                                                    :class="{'is-invalid' : errors[index+1]?.source != ''}" v-else>
                                                    <span v-if="if_on_admin_screen">
                                                        <button class="btn btn-secondary p-2" type="button"
                                                            data-bs-toggle="dropdown" aria-expanded="false"
                                                            :disabled="!if_allow_edit" style="width: auto !important;">
                                                            {{ column.source_column.main_category }}
                                                        </button>
                                                        <ul class="dropdown-menu bg-white">
                                                            <li><a class="dropdown-item" href="#"
                                                                    @click.prevent="handleMainCategoryChange(column, 'Columns')">Column</a>
                                                            </li>
                                                            <li><a class="dropdown-item" href="#"
                                                                    @click.prevent="handleMainCategoryChange(column, 'Static')">Static</a>
                                                            </li>
                                                            <li><a class="dropdown-item" href="#"
                                                                    @click.prevent="handleMainCategoryChange(column, 'Dropdown')">Dropdown</a>
                                                            </li>
                                                        </ul>
                                                    </span>
                                                    <input type="text" class="form-control"
                                                        v-model="column.source_column.value"
                                                        :class="{'is-invalid' : errors[index+1]?.source != ''}"
                                                        v-if="isConstant(column) || (if_on_admin_screen && (column.source_column.main_category == 'Dropdown' || column.source_column.main_category == 'Static'))"
                                                        @change="updateConstant(column)" :key="column?.id || index"
                                                        :disabled="!if_allow_edit || (column.source_column.main_category == 'Dropdown'  && if_on_admin_screen)" aria-label="Value">
                                                    <accordion-select v-model="column.source_column.value"
                                                        :key="column.source_column?.id || index"
                                                        :class="{'is-invalid' : errors[index+1]?.source != ''}" v-else
                                                        :options="source_columns"
                                                        :if_allow_edit="if_allow_edit"></accordion-select>
                                                    <button class="btn btn-secondary ps-2" data-bs-toggle="tooltip"
                                                        title="Use a value from a column" @click="toggleConstant(column)"
                                                        v-if="isConstant(column) && !if_on_admin_screen"
                                                        :disabled="!if_allow_edit">
                                                        <img class="icon" src="/img/icons/avid-icons/map.svg" alt="">
                                                    </button>
                                                    <button class="btn btn-secondary ps-2" data-bs-toggle="tooltip"
                                                        title="Use a constant value" @click="toggleConstant(column)"
                                                        v-else-if="!if_on_admin_screen" :disabled="!if_allow_edit">
                                                        <img class="icon" src="/img/icons/avid-icons/edit.svg" alt="">
                                                    </button>
                                                </div>
                                                <div class="text-danger invalid-feedback"
                                                    v-if="errors[index+1]?.source != ''">
                                                    {{ errors[index+1].source }}
                                                </div>
                                            </td>
                                            <td class="mapping-grid_map_to">
                                                <div class="d-flex align-items-center">
                                                    <small>Maps to</small>
                                                </div>
                                            </td>
                                            <td class="mapping-grid__destination">
                                                <div class="d-flex mb-2">
                                                    <input type="text" class="form-control" v-if="!isColumnOptional(column)"
                                                        :value="getColumnText(column.destination_column)"
                                                        disabled="disabled" aria-label="Text input">
                                                    <accordion-select class="ms-2 ms-md-0" v-else
                                                        v-model="column.destination_column"
                                                        :class="{'is-invalid' : errors[index+1]?.destination != ''}"
                                                        :options="getAvailableDestinations(column.destination_column)"
                                                        :key="'dest'+(column?.id || index)"
                                                        :if_allow_edit="if_allow_edit"></accordion-select>
                                                </div>
                                                <div class="text-danger invalid-feedback"
                                                    v-if="errors[index+1]?.destination != ''">
                                                    {{ errors[index+1].destination }}
                                                </div>
                                            </td>
                                            <td class="mapping-grid__action">
                                                <button v-if="isColumnOptional(column) && if_allow_edit"
                                                    class="btn btn-white btn-icon-only-md" @click="removeColumn(index+1);">
                                                    <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                                </button>
                                                <div v-else-if="isColumnOptional(column) && !if_allow_edit"
                                                    class="d-inline-block" data-bs-toggle="tooltip"
                                                    title="You do not have privileges to complete this action">
                                                    <button class="btn btn-secondary btn-icon-only-md" :disabled="true">
                                                        <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div class="d-none d-xl-block drill-down mb-3" :class="(is_expanded ? 'col-12' : 'col-4')" v-if="client">
                        <preview-table :client="client" :source="local_source" :source_columns="source_columns"
                            :data_type="null"></preview-table>
                    </div>
                </div>

                <!-- md and smaller screens : card/grouped layout -->
                <div class="d-md-none">
                    <template v-for="(column, index) in slicedMapping" :key="column?.id || index">
                        <div v-if="showColumn(column, index+1)" class="card py-2 px-3 mb-3"
                            style="background-color: #FAFAFA;">
                            <div class="d-flex justify-content-between align-items-center">
                                <strong>Column {{ index+1 }}</strong>
                                <div class="mapping-grid__action">
                                    <button v-if="isColumnOptional(column) && if_allow_edit" class="btn btn-none px-0"
                                        @click="removeColumn(index+1)">
                                        <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                    </button>
                                    <div v-else-if="isColumnOptional(column) && !if_allow_edit" class="d-inline-block"
                                        data-bs-toggle="tooltip"
                                        title="You do not have privileges to complete this action">
                                        <button class="btn btn-none px-0" :disabled="true">
                                            <img class="icon" src="/img/icons/avid-icons/trash.svg" alt="">
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Source column -->
                            <div class="mb-2 w-100">
                                <label class="form-label">Column Name You're Importing</label>
                                <div class="mapping-grid__source">
                                    <select v-if="isDropdown(column)" v-model="column.source_column.value"
                                        class="form-select w-100" :disabled="!if_allow_edit" aria-label="Column Name You're Importing">
                                        <option v-for="option in dropdowns[column.destination_column]"
                                            :value="option.id">{{ option.name }}</option>
                                    </select>
                                    <div class="input-group w-100"
                                        :class="{'is-invalid' : errors[index+1]?.source != ''}" v-else>
                                        <input type="text" class="form-control" v-model="column.source_column.value"
                                            :class="{'is-invalid' : errors[index+1]?.source != ''}"
                                            v-if="isConstant(column)" @change="updateConstant(column)"
                                            :key="column?.id || index" :disabled="!if_allow_edit" aria-label="Value">
                                        <accordion-select v-else v-model="column.source_column.value"
                                            :key="column.source_column?.id || index"
                                            :class="{'is-invalid' : errors[index+1]?.source != ''}"
                                            :options="source_columns" :if_allow_edit="if_allow_edit"
                                            style="width: calc(100% - 46px);"></accordion-select>
                                        <button class="btn btn-secondary ps-2" data-bs-toggle="tooltip"
                                            title="Use a value from a column" @click="toggleConstant(column)"
                                            v-if="isConstant(column)" :disabled="!if_allow_edit">
                                            <img class="icon" src="/img/icons/avid-icons/map.svg" alt="">
                                        </button>
                                        <button class="btn btn-secondary ps-2" data-bs-toggle="tooltip"
                                            title="Use a constant value" @click="toggleConstant(column)" v-else
                                            :disabled="!if_allow_edit">
                                            <img class="icon" src="/img/icons/avid-icons/edit.svg" alt="">
                                        </button>
                                    </div>
                                    <div class="text-danger invalid-feedback" v-if="errors[index+1]?.source != ''">
                                        {{ errors[index+1].source }}
                                    </div>
                                </div>
                            </div>

                            <!-- Destination column -->
                            <div class="mb-1">
                                <label class="form-label">{{ destination?.integration?.name || 'Destination' }}
                                    Column</label>
                                <div class="mapping-grid__destination w-100">
                                    <div class="mb-2 w-100 d-flex flex-column">
                                        <input type="text" class="form-control w-100" v-if="!isColumnOptional(column)"
                                            :value="getColumnText(column.destination_column)" disabled="disabled" aria-label="Text input">
                                        <accordion-select class="w-100" v-else v-model="column.destination_column"
                                            :class="{'is-invalid' : errors[index+1]?.destination != ''}"
                                            :options="getAvailableDestinations(column.destination_column)"
                                            :key="'dest'+(column?.id || index)"
                                            :if_allow_edit="if_allow_edit"></accordion-select>
                                    </div>
                                    <div class="text-danger invalid-feedback" v-if="errors[index+1]?.destination != ''">
                                        {{ errors[index+1].destination }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'PathwaysColumnMapping',
    props: {
        client: {
            type: Object,
        },
        destination: {
            type: Object,
        },
        modelValue: {
            type: Array,
            default: () => []
        },
        dest_columns: {
            type: Array,
            default: () => []
        },
        source: {
            type: [Object, Array],
        },
        source_columns: {
            type: Array,
            default: () => []
        },
        show_unmapped_only: {
            type: Boolean,
            default: false
        },
        if_allow_edit: {
            type: Boolean,
            default: true
        },
        if_on_admin_screen: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            mapping: [],
            errors: [],
            local_source: {
                source: null,
                operation: null,
                data_source: null,
                prioritization_column: null
            },
            is_expanded: false,
            keep_showing: [],
            local_pk_source_columns: [],
            flat_dest_columns:[],
            flat_primary_columns:[],
            prioritization_column:[{
                column: '',
                sort: 'DESC'
            }],
            dropdowns: [],
        };
    },
    beforeMount() {
        // Initialize local_source with safe defaults
        this.local_source = {
            source: null,
            operation: null,
            data_source: null,
            prioritization_column: null
        };

        // Safely handle source prop
        if (this.source) {
            if (typeof this.source === 'string' || this.source instanceof String) {
                this.local_source.source = this.source;
            } else {
                // Safely copy properties with null checks
                this.local_source = {
                    source: this.source.source || null,
                    operation: this.source.operation || null,
                    data_source: this.source.data_source || null,
                    prioritization_column: this.source.prioritization_column || null,
                    id: this.source.id || null
                };
            }
        }

        // Safely access prioritization_column
        if (this.local_source.prioritization_column != null) {
            this.prioritization_column = [...this.local_source.prioritization_column];
        }

        this.setupUI();

        //For the error message, set the default value to false.
        this.errors = [];
        for(let i = 0; i < this.mapping.length; i++) {
            this.errors.push({
                source: "",
                destination: ""
            });
        }
        this.local_pk_source_columns = this.getFormattedColumns(this.source_columns || []);
    },
    mounted() {
        //Ensure the tooltips show up
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (let i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    // Hide the tooltip using the index passed to the closure
                    tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    // Hide the tooltip using the index passed to the closure
                    tooltipList[index].hide();
                });
            })(i);
        }
    },
    watch: {
        modelValue: {
            handler(val) {
                if(val != null)
                    this.mapping = val;
                else
                    this.mapping = this.getDefaultMapping();
            },
            immediate: true
        },
        mapping: {
            handler(n, o) {
                this.$emit('update:modelValue', this.mapping);
                this.$emit('changeMappingDetails', this.mapping);
                if (this.mapping.length > this.errors.length){
                    for(let i = 0; i < this.mapping.length - this.errors.length; i++) {
                        this.errors.push({
                            source: "",
                            destination: ""
                        });
                    }
                }
            },
            deep: true
        },
        prioritization_column: {
            handler() {
                this.$emit('prioritize', this.prioritization_column);
            },
            deep: true
        },
        dest_columns: {
            handler() {
                this.flat_dest_columns = this.getDestinationColumns();
                this.flat_primary_columns = this.getDestinationColumns(true);
                // Rebuild mappings when destination object changes (e.g. switching to custom object)
                this.setupUI();
                this.$forceUpdate();
            },
            deep: true
        },
    },
    computed: {
        slicedMapping() {
            return this.mapping.slice(1);
        }
    },
    methods: {
        isModel() {
            //The model update will always be 7 "folders" long
            const urlString = window.location.href;
            // Parse the URL
            const url = new URL(urlString);
            // Split the pathname part of the URL on '/', filter out empty segments
            const segments = url.pathname.split('/').filter(segment => segment !== '');
            // Get the seventh segment (the model ID) if there is one
            return segments.length == 7;
        },
        showColumn(col, index){
            if(!this.show_unmapped_only) return true;

            if(this.keep_showing.length > index && this.keep_showing[index] == true)
                return true;

            if (!col || !col.source_column) return false;

            //If the first one is an array but the first value isn't set
            const show_primary = (index == 0 && Array.isArray(col.source_column) && col.source_column.length > 0 && (col.source_column[0]?.value == null || col.source_column[0]?.value == ""));
            //If it is a normal object but the value hasn't been set
            const show_other = col.source_column && (col.source_column.value == null || col.source_column.value == "");

            return show_primary || show_other;
        },
        showAdditionalColumns(){
            if(!this.show_unmapped_only) return true;

            if (!this.mapping || !Array.isArray(this.mapping)) return false;

            for(let i = 1; i < this.mapping.length; i++){
                if(this.showColumn(this.mapping[i], i))
                    return true;
            }

            return false;
        },
        addPriorityColumn() {
            if(!this.prioritization_column || !Array.isArray(this.prioritization_column)) return;
            
            this.prioritization_column.push({
                column: '',
                sort: 'DESC'
            });
        },
        setPrioritization() {
            // IMPORTANT:
            // This component should not mutate / remove already-saved prioritization columns on load.
            // We only normalize the shape so the UI renders consistently and saving works reliably.

            if (!Array.isArray(this.prioritization_column)) {
                this.prioritization_column = [];
            }

            this.prioritization_column = this.prioritization_column
                .filter((priority) => priority && typeof priority === 'object')
                .map((priority) => {
                    const column =
                        priority.column && typeof priority.column === 'object'
                            ? priority.column.value
                            : (priority.column ?? '');

                    return {
                        column,
                        sort: priority.sort === 'ASC' ? 'ASC' : 'DESC',
                    };
                });

            if (this.prioritization_column.length === 0) {
                this.prioritization_column = [{
                    column: '',
                    sort: 'DESC',
                }];
            }

            this.$emit('prioritize', this.prioritization_column);
        },
        toggleExpand() {
            this.is_expanded = !this.is_expanded;
        },
        resetMapping() {
            this.$emit('update:modelValue', null);
        },
        setupUI() {
            this.flat_dest_columns = this.getDestinationColumns();
            this.flat_primary_columns = this.getDestinationColumns(true);

            //Set the default columns or whatever was sent to me
            if(this.modelValue != null && this.modelValue.length > 0) {
                this.mapping = this.modelValue;
                const def = this.getDefaultMapping();

                //Remove any columns that are no longer available
                if(this.mapping && Array.isArray(this.mapping)) {
                    for(let i = 0; i < this.mapping.length; i++){
                        if(!this.mapping[i]) continue;
                        //If it is an array...
                        if(Array.isArray(this.mapping[i].source_column)) {
                            for(let j = 0; j < this.mapping[i].source_column.length; j++) {
                                if(!this.mapping[i].source_column[j]) continue;
                                                            //If the value is in the list of source_columns
                                if(this.mapping[i].source_column[j].main_category=="Columns" && this.source_columns && Array.isArray(this.source_columns) && !this.source_columns.some(obj => obj.value === this.mapping[i].source_column[j].value)){
                                    this.mapping[i].source_column[j] = this.getBlankSourceColumn();
                                    break;
                                }
                            }
                        }
                        //Just look at the object
                        else if(this.mapping[i].source_column && this.mapping[i].source_column.main_category=="Columns" && this.source_columns && Array.isArray(this.source_columns) && !this.source_columns.some(obj => obj.value === this.mapping[i].source_column.value)){
                            //If it is the primary column, it should be an array
                            if(this.mapping[i].is_primary_identifier == 1)
                                this.mapping[i].source_column = [this.getBlankSourceColumn()];
                            else
                                this.mapping[i].source_column = this.getBlankSourceColumn();
                        }
                    }
                }

                //Add any missing required fields
                if(def && Array.isArray(def)) {
                    for(let i = 0; i < def.length; i++){
                        if(!def[i]) continue;
                        let is_mapped = false;
                        if(this.mapping && Array.isArray(this.mapping)) {
                            for(let j = 0; j < this.mapping.length; j++) {
                                if(!this.mapping[j]) continue;
                                if(def[i]['destination_column'] == this.mapping[j]['destination_column']){
                                    is_mapped = true;
                                    break;
                                }
                            }
                        }
                        //Primary needs to be inserted in the beginning
                        if(!is_mapped && def[i]['is_primary_identifier'] == 1 && (this.mapping.length == 0 || (this.mapping[0] && this.mapping[0].is_primary_identifier == false))){
                            this.mapping.unshift(def[i]);
                        }
                        else if(!is_mapped && def[i]['is_primary_identifier'] == 1 && this.mapping[0].destination_column == null){
                            this.mapping[0] = def[i];
                        }
                        //Otherwise, put it at the end
                        else if(!is_mapped && def[i]['destination_column'] != null){
                            this.mapping.push(def[i]);
                        }
                    }
                }
            }
            else
                this.mapping = this.getDefaultMapping();

            this.keep_showing = [];
            if(this.mapping && Array.isArray(this.mapping)) {
                for(let i = 0; i < this.mapping.length; i++) {
                    if(this.mapping[i]) {
                        this.keep_showing.push(this.showColumn(this.mapping[i], i));
                    }
                }
            }
            
            if(this.local_source && this.local_source.source) {
                this.setPrioritization();
            }
        },
        refreshColumns() {
            this.$emit('refresh');
        },
        getFormattedColumns(db_columns) {
            const columns = [];
            if (!db_columns || !Array.isArray(db_columns)) {
                return columns;
            }
            db_columns.forEach(column => {
                if (column) {
                    column.main_category = "Columns";
                    columns.push(column);
                }
            });
            return columns;
        },
        getAvailableDestinations(column, primary_key = false) {
            const dest_array = primary_key ? this.flat_primary_columns : this.flat_dest_columns;

            const arr = [];
            //Loop through the mapping variable and see if the column is used
            if (!dest_array || !Array.isArray(dest_array)) {
                return arr;
            }
            for(let j = 0; j < dest_array.length; j++){
                const dest = dest_array[j];
                if (!dest) continue;
                let found = false;
                for(let i = 0; i < this.mapping.length; i++) {
                    if(this.mapping[i] && this.mapping[i].destination_column == dest.value && column != dest.value) {
                        found = true;
                        break;
                    }
                }

                if((primary_key && dest.can_be_key) || (!found && !primary_key))
                    arr.push(dest);
            }
            return arr;
        },
        getPrimayKeyDestinations(column = null) {
            const arr = this.getAvailableDestinations(column, true);
            if(arr.length == 1)
                return arr[0].value;

            return arr;
        },
        selectKeyColumn(event, index){
            if(!this.mapping || !this.mapping[index] || !this.mapping[index].source_column || !Array.isArray(this.mapping[index].source_column)) return;
            
            let last_element = this.mapping[index].source_column[this.mapping[index].source_column.length - 1];
            if(!last_element || !last_element.value) { // the last element is a search inputEvent, we should update it
                this.mapping[index].source_column[this.mapping[index].source_column.length - 1] = event;
            } else { // the last element is a value, we should add a new element
                this.mapping[index].source_column.push(event);
            }
        },
        hasOneKeyOption() {
            const arr = this.getPrimayKeyDestinations();
            //See if arr is an array or a single value
            if(!Array.isArray(arr)){
                return true;
            }
            return false;
        },
        isColumnOptional(column) {
            if (!column || !column.destination_column) return true;
            
            const foundInstance = this.flat_dest_columns.find(item => item.value === column.destination_column);

            if(foundInstance == null)
                return true;

            return foundInstance.optional;
        },
        getColumnText(column) {
            if (!column) return '';
            
            const foundInstance = this.flat_dest_columns.find(item => item.value === column);

            if(foundInstance == null)
                return column;

            return foundInstance.text;
        },
        canBeKey(column) {
            if (!column || !column.destination_column) return false;
            
            const foundInstance = this.flat_dest_columns.find(item => item.value === column.destination_column);

            if(foundInstance == null)
                return false;

            return foundInstance.can_be_key;
        },
        getDefaultMapping() {
            const arr = [];
            //Add one for the primary key
            if(this.hasOneKeyOption()){
                const val = this.getPrimayKeyDestinations();
                arr.push(this.getMapping(val, true));
            }
            else
                arr.push(this.getMapping(null, true));

            //Add any other non-optional columns
            for(let i = 0; i < this.flat_dest_columns.length; i++) {
                //Also make sure the values in arr[].destination_column aren't equal to this.flat_dest_columns[i]
                if(!this.flat_dest_columns[i].optional && !arr.some(obj => obj.destination_column === this.flat_dest_columns[i].value))
                    arr.push(this.getMapping(this.flat_dest_columns[i].value));
            }

            //If it is only the primary key, add one more mapping for them to set up
            if(arr.length == 1)
                arr.push(this.getMapping(null));

            return arr;
        },
        getMapping(col, is_primary = false) {
            return {
                id: -1 * Math.random(),
                client_id: this.client?.id || null,
                census_connection_id: this.local_source?.id || null,
                source_column: this.getBlankSourceColumn(),
                destination_column: col,
                is_primary_identifier: is_primary,
            }
        },
        getBlankSourceColumn() {
            return {
                main_category: 'Columns',
                category: null,
                type: 'string',
                value: null,
                text: ""
            };
        },
        getDestinationColumns(is_primary = false) {
            const arr = [];
            const textSet = new Set(); // Set to track the text properties

            //TODO: exclude non-updatable columns if it isn't primary key and is an upsert / update

            // Loop through dest_columns
            if (!this.dest_columns || !Array.isArray(this.dest_columns)) {
                return arr;
            }
            for(let i = 0; i < this.dest_columns.length; i++) {
                const column = this.dest_columns[i];
                if (!column) continue;
                if((is_primary || column.updateable) && !column.is_static_list_reference) {
                    let key = 'can_be_' + (this.local_source?.operation || '') + '_key';
                    if(this.local_source?.operation === 'mirror') key = 'can_be_upsert_key';
                    else if(this.local_source?.operation === 'delete') key = 'can_be_update_key';
                    else if(this.local_source?.operation === 'create' || this.local_source?.operation === 'append') key = 'can_be_insert_key';

                    // Check if the text property already exists
                    if(!textSet.has(column.label) && column.type != 'Unmappable') {
                        arr.push({
                            value: column.full_name,
                            text: column.label,
                            category: null,
                            optional: !column.required_for_mapping,
                            can_be_key: column[key] != null && column[key] === true,
                        });
                        textSet.add(column.label); // Add text to the Set
                    }
                }
            }

            // Sort the arr by text
            arr.sort((a, b) => a.text.localeCompare(b.text));

            return arr;
        },
        updateConstant(column) {
            if(!column || !column.source_column) return;
            column.source_column.main_category = 'Static';
        },
        toggleConstant(column) {
            if(!column || !column.source_column) return;
            
            //If it is a constant, switch back to the column
            if(this.isConstant(column))
                column.source_column.main_category = 'Columns';
            else
                column.source_column.main_category = 'Static';

            column.source_column.value = "";
        },
        isConstant(column) {
            if(!column || !column.source_column) return false;
            if(Array.isArray(column.source_column)) return false;

            return column.source_column != null && column.source_column.main_category == 'Static';
        },
        isValid() {
            //Loop through the mapping and make sure that each column has a source and destination
            let valid = true;

            if(!this.mapping || !Array.isArray(this.mapping) || !this.errors || !Array.isArray(this.errors)) {
                return false;
            }

            for(let i = 0; i < this.mapping.length; i++) {
                if(!this.mapping[i] || !this.mapping[i].source_column) continue;
                
                //There isn't a column selected
                if((this.mapping[i].source_column.value == null || this.mapping[i].source_column.value == "" ) && this.mapping[i].source_column.main_category == 'Columns') {
                    if(this.errors[i]) this.errors[i].source = "Please select a source column";
                    valid = false;
                }
                //There is a blank value
                else if(!Array.isArray(this.mapping[i].source_column) && (this.mapping[i].source_column.value == null || this.mapping[i].source_column.value == "")) {
                    if(this.errors[i]) this.errors[i].source = "Please enter a static value above";
                    valid = false;
                }
                //The column has been removed
                else if (this.mapping[i].source_column.main_category == 'Columns') {
                    if (typeof this.mapping[i].source_column.value == 'object' && this.source_columns && Array.isArray(this.source_columns) && !this.source_columns.some(obj => obj.value === this.mapping[i].source_column.value.value)) {
                        if(this.errors[i]) this.errors[i].source = "The previously mapped column has been removed from the source data. Please select another column.";
                        valid = false;
                    } else if (typeof this.mapping[i].source_column.value == 'string' && this.source_columns && Array.isArray(this.source_columns) && !this.source_columns.some(obj => obj.value === this.mapping[i].source_column.value)) {
                        if(this.errors[i]) this.errors[i].source = "The previously mapped column has been removed from the source data. Please select another column.";
                        valid = false;
                    }
                }
                else
                    if(this.errors[i]) this.errors[i].source = "";

                if(this.local_source?.operation != 'append' && (this.mapping[i].destination_column == null || this.mapping[i].destination_column == "")) {
                    if(this.errors[i]) this.errors[i].destination = "Please select a destination column";
                    valid = false;
                }
                else if(this.local_source?.operation == 'append' && i == 0){
                    const val = this.getPrimayKeyDestinations(null);
                    if(Array.isArray(val) && val.length == 1)
                        this.mapping[i].destination_column = val[0].value;
                    else if(!Array.isArray(val))
                        this.mapping[i].destination_column = val;
                    else
                        this.mapping[i].destination_column = 'unique_id';
                    if(this.errors[i]) this.errors[i].destination = "";
                }
                else
                    if(this.errors[i]) this.errors[i].destination = "";
            }

            return valid;
        },
        removeColumn(i) {
            if(!this.mapping || !Array.isArray(this.mapping) || !this.errors || !Array.isArray(this.errors)) return;
            
            //Remove the column
            this.mapping.splice(i, 1);
            this.errors.splice(i, 1);
            this.$forceUpdate();
        },
        isDropdown(column) {
            if(!column || !column.source_column || !column.destination_column) return false;
            
            // If we're on admin screen, never treat it as a dropdown
            if(this.if_on_admin_screen) return false;
            
            if(column.source_column != null && (column.source_column.main_category == 'Dropdown' || column.source_column.main_category == 'Dropdowns')) {
                if(this.dropdowns[column.destination_column] != null)
                    return true;

                //Make the array index
                this.dropdowns[column.destination_column] = [];

                //Then populate that array
                const form_data = {
                    column: column.destination_column,
                    connection_id: this.destination?.id
                }

                window.axios.post('/api/census/get_dropdown_options', form_data)
                    .then(response => {
                        if(response.data.objects != null)
                            this.dropdowns[column.destination_column] = response.data.objects;
                        else
                            column.source_column.main_category = 'Static';
                        this.$forceUpdate();
                    })
                      .catch(error => {
                        column.source_column.main_category = 'Static';
                        this.$forceUpdate();
                      });

                return true;
            }
            return false;
        },
        handleMainCategoryChange(column, newCategory) {
            if(!column || !column.source_column) return;
            
            // Update the main category
            column.source_column.main_category = newCategory;
            
            // Clear the value when changing categories
            column.source_column.value = null;
            
            // If switching to dropdown, trigger the dropdown population
            if(newCategory === 'Dropdown') {
                this.isDropdown(column);
            }
            
            this.$forceUpdate();
        }
    }
});
</script>
